import React, { useState, Fragment } from "react";
import HoverIconButton from "components/HoverIconButton";
import classnames from "classnames";
import { useFormikContext } from "formik";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import ModalActions from "components/ModalActions";
import Grip from "components/Grip";
import { removeProduct } from "utils/product";
import { STATE_KEYS } from "utils/constants/state";
import { COLORS } from "utils/styles";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import {
  TOOLTIP_POSITION,
  ACTIVE_HOVER_BUTTON_CLASSES
} from "utils/constants/ui";
import { usePageBuilderContext } from "utils/context";
import Analytics, {
  HIGHLIGHT_PRODUCT,
  ADD_PRODUCT_TO_TABLE,
  REMOVE_PRODUCT_FROM_TABLE
} from "utils/analytics";
import Image from "components/Image";
import { PRIORITY_FIT_COVER } from "utils/constants/ui";
import { DIMENSIONS } from "utils/constants/image";

const ProductListItem = ({
  uid,
  name,
  title,
  images,
  collapseImages,
  layoutIndex,
  canRemove,
  canAdd,
  draggable,
  canEmphasize,
  onClick,
  snapshot
}) => {
  const { tracking } = usePageBuilderContext();
  const [showModal, setShowModal] = useState(false);
  const [hover, setHover] = useState();

  const {
    values,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const productsPath = `${name}.data.products`;
  const emphasisIndex = get(values, STATE_KEYS.MANIFEST.PRICING_EMPHASIS_INDEX);
  const isEmphasized = emphasisIndex === layoutIndex;
  const layout = get(values, STATE_KEYS.MANIFEST.PRICING_LAYOUT);

  const containerProps = {
    className: classnames(
      "flex flex-row justify-between items-center ph3 pv2 bh--inset relative overflow-hidden",
      {
        "pointer": draggable,
        "bg-content--secondary": hover,
        "bg-white": !hover
      }
    ),
    onMouseEnter: () => !hover && draggable && setHover(true),
    onMouseLeave: () => hover && draggable && setHover(false)
  };

  const titleProps = {
    className: classnames("f6 fw5 w-100 pv3 pl2 link__underline pointer", {
      pl3: canEmphasize
    }),
    onClick
  };

  let action;
  if (canRemove) {
    action = (
      <HoverIconButton
        type="close"
        tooltip="Remove"
        tooltipPosition={TOOLTIP_POSITION.RIGHT}
        onClick={() => {
          Analytics.track(REMOVE_PRODUCT_FROM_TABLE, {
            ...tracking,
            product: uid
          });
          setShowModal(true);
        }}
      />
    );
  } else if (canAdd) {
    action = (
      <HoverIconButton
        type="add"
        tooltip="Add to pricing table"
        tooltipPosition={TOOLTIP_POSITION.RIGHT}
        onClick={() => {
          Analytics.track(ADD_PRODUCT_TO_TABLE, {
            ...tracking,
            product: uid
          });
          const updatedLayout = cloneDeep(layout);
          updatedLayout.push({
            uid
          });

          setFieldValue(STATE_KEYS.MANIFEST.PRICING_LAYOUT, updatedLayout);
          setFieldTouched(STATE_KEYS.MANIFEST.PRICING_LAYOUT, true);
        }}
      />
    );
  }

  const highlightButtonProps = {
    type: "star",
    title,
    tooltip: "Highlight",
    color: isEmphasized ? COLORS.white : COLORS.deselected,
    onClick: () => {
      Analytics.track(HIGHLIGHT_PRODUCT, {
        ...tracking,
        product: uid
      });
      const emphasisValue = isEmphasized ? null : layoutIndex;

      setFieldValue(STATE_KEYS.MANIFEST.PRICING_EMPHASIS_INDEX, emphasisValue);
      setFieldTouched(STATE_KEYS.MANIFEST.PRICING_EMPHASIS_INDEX, true);
    }
  };
  if (isEmphasized) {
    highlightButtonProps.customClasses = ACTIVE_HOVER_BUTTON_CLASSES;
  }
  const bottomBorderAccent = (
    <div className="ph3">
      <div className="bb hairline-1 w-100"></div>
    </div>
  );
  const hasImages = images && images.length > 0;

  return (
    <Fragment>
      <div {...containerProps}>
        <Grip hover={hover} snapshot={snapshot} />
        {canEmphasize && <HoverIconButton {...highlightButtonProps} />}
        <div className="w-100 flex items-center justify-center">
          {!collapseImages && (
            <div
              style={DIMENSIONS.PRODUCT_LIST_ITEM}
              className={classnames("br2 overflow-hidden flex-shrink-0", {
                "bg-whitesmoke": !hasImages
              })}
            >
              {hasImages ? (
                <Image
                  src={images[0]}
                  {...DIMENSIONS.PRODUCT_LIST_ITEM}
                  alt={title}
                  {...PRIORITY_FIT_COVER}
                />
              ) : null}
            </div>
          )}
          <div {...titleProps}>{title}</div>
        </div>
        <div className="flex flex-shrink-0 items center justify-center">
          {action}
        </div>
      </div>
      {showModal && (
        <Modal showModal={showModal} onClose={() => setShowModal(false)}>
          <ModalContent
            slides={[
              {
                title: "Remove product",
                header:
                  "Are you sure you want to remove this product from the pricing table?"
              }
            ]}
          >
            <ModalActions
              actions={[
                {
                  cta: "Cancel",
                  onClick: () => {
                    setShowModal(false);
                  }
                },
                {
                  cta: "Yes",
                  onClick: removeProduct({
                    values,
                    uid,
                    setValues,
                    setFieldTouched,
                    productsPath,
                    layoutPath: STATE_KEYS.MANIFEST.PRICING_LAYOUT
                  })
                }
              ]}
            />
          </ModalContent>
        </Modal>
      )}
      {bottomBorderAccent}
    </Fragment>
  );
};

export default ProductListItem;
